import {
  GeckoComponentRect,
  GeckoMarkupIcon,
  GeckoMarkupLabel,
  GeckoMarkupLayer,
  GeckoMarkupPrimitive,
} from 'types'

/**
 * Computes the bounding rect to frame the camera on for a markup layer, for the
 * "zoom to markup" panel action.
 *
 * FFS layers have a single shape primitive (indication rect / CTP line / flaw
 * polygon) plus, for indications, a title label. We frame the SHAPE, ignoring
 * label/icon point-markers.
 *
 * For the common single-shape case we return that primitive's `.bounds`
 * DIRECTLY, which preserves wrapped-rect semantics (a rect can span the cylinder
 * seam with `start > end`). Only when a layer has multiple shape primitives do
 * we fall back to a projected min/max union (best-effort; does not handle wrap).
 */
export function layerBoundingRect(
  layer: GeckoMarkupLayer
): GeckoComponentRect | undefined {
  const shapes = layer.data.allPrimitives.filter(
    (p: GeckoMarkupPrimitive) =>
      !(p instanceof GeckoMarkupLabel) && !(p instanceof GeckoMarkupIcon)
  )
  if (shapes.length === 0) return undefined
  if (shapes.length === 1) return shapes[0].bounds

  // Fallback: union several shape primitives. Project all to the first shape's
  // space so the numbers are comparable, then take min-start / max-end.
  const space = shapes[0].bounds.space
  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity
  for (const shape of shapes) {
    const b = shape.bounds.project(space)
    minX = Math.min(minX, b.start.x.num)
    minY = Math.min(minY, b.start.y.num)
    maxX = Math.max(maxX, b.end.x.num)
    maxY = Math.max(maxY, b.end.y.num)
  }
  const first = shapes[0].bounds.project(space)
  return GeckoComponentRect.CreateFromXYs(
    first.componentID,
    minX,
    minY,
    maxX,
    maxY,
    first.start.x.unit,
    first.start.y.unit
  )
}
